import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NewBaseTable, { ColumnConfig } from 'components/NewBaseTable/NewBaseTable';
import { WidgetUi } from 'common/ui';
import { capitalizeFirst } from 'common/helpers/v1';
import { SiteTableType } from 'types/protein';
import {
  CounterItem,
  IconContainer,
  LineMachineStateIconContainer,
  SiteTableWidgetContainer
} from './SiteTable.element';
import { useHistory } from 'react-router-dom';
import { useBuSettings } from 'common/pages/fleetV2/providers';
import { useLineStatusInfo } from '../hooks/useLineStatusInfo';
import { useMachineStatusInfo } from '../hooks/useMachineStatusInfo';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';

interface Props {
  plantId: string;
  type: SiteTableType;
  businessUnit?: string;
}

const formatMillisecondsToHMS = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const h = String(hours).padStart(2, '0');
  const m = String(minutes).padStart(2, '0');
  const s = String(seconds).padStart(2, '0');

  return `${h}h ${m}m ${s}s`;
};

export const SiteTable = ({ plantId, type, businessUnit }: Props): JSX.Element => {
  const { t } = useTranslation(['mh']);

  const { colors, Icons } = useBuSettings('protein');

  const { data: lines, isLoading: linesLoading } = useLineStatusInfo({ plantId, type });

  const { data: machines, isLoading: machinesLoading } = useMachineStatusInfo({
    plantId,
    type
  });

  const transformedData = useMemo(() => {
    if (type === 'LINE') {
      return lines?.map((line) => ({
        id: line.id,
        name: line.name,
        status: line.status,
        currentAlarms: line.numCurrentAlarms,
        past24hAlarms: line.numAlarmsOverPeriod,
        production24h: line.productionOverPeriod,
        utilization24h: line.utilizationOverPeriod != null ? `${line.utilizationOverPeriod}%` : null
      }));
    }

    return machines?.map((machine) => ({
      id: machine.id,
      name: machine.description,
      status: machine.status,
      currentAlarms: machine.numCurrentAlarms,
      past24hAlarms: machine.numAlarmsOverPeriod,
      configType: machine.configurationType,
      line: machine.lineName,
      production24h: machine.productionOverPeriod,
      utilization24h:
        machine.utilizationOverPeriod != null ? `${machine.utilizationOverPeriod}%` : null
    }));
  }, [type, lines, machines]);

  const generateColumnsTable = (): ColumnConfig[] => {
    if (type === 'LINE') {
      return [
        {
          header: capitalizeFirst(t('Line')),
          id: 'name',
          isEnableSorting: true,
          isSelected: true,
          renderer: ({ row, getValue }) => {
            const history = useHistory();
            const openIssuesOnclick = (machineId: string) => {
              history.push(`/fleet/line/v2/${machineId}`);
            };
            const itemId = row.original?.id as string;
            const currentAlarms = row.original?.currentAlarms;
            return (
              <button onClick={() => openIssuesOnclick(itemId)}>
                {getValue()}
                {currentAlarms > 0 && (
                  <IconContainer>
                    <TriangleWarning />
                    <CounterItem>{currentAlarms}</CounterItem>
                  </IconContainer>
                )}
              </button>
            );
          }
        },
        {
          header: capitalizeFirst(t('Status')),
          id: 'status',
          isEnableSorting: true,
          isSelected: true,
          renderer: ({ getValue }) => {
            const value = getValue();
            if (!value) {
              return <p>-</p>;
            }
            const Icon = (Icons && Icons[value]) || <></>;
            const color = (colors && colors[value]) || '';
            return (
              <>
                <LineMachineStateIconContainer className="machine-state-icon" {...{ color }}>
                  {Icon} <span className="label">{capitalizeFirst(value)}</span>
                </LineMachineStateIconContainer>
              </>
            );
          }
        },
        {
          header: capitalizeFirst(t('Alarms (Past 24h)')),
          id: 'past24hAlarms',
          isEnableSorting: true,
          isSelected: true,
          renderer: ({ getValue }) => <p>{getValue() ?? '-'}</p>
        },
        {
          header: capitalizeFirst(t('Production (Past 24h)')),
          id: 'production24h',
          isEnableSorting: true,
          isSelected: true,
          renderer: ({ getValue }) => {
            const value = getValue();
            return value ? <p>{formatMillisecondsToHMS(value)}</p> : '-';
          }
        },
        {
          header: capitalizeFirst(t('Utilization (Past 24h)')),
          id: 'utilization24h',
          isEnableSorting: true,
          isSelected: true,
          renderer: ({ getValue }) => <p>{getValue() || '-'}</p>
        }
      ];
    }

    return [
      {
        header: capitalizeFirst(t('Machine')),
        id: 'name',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ row, getValue }) => {
          const history = useHistory();
          const openIssuesOnclick = (machineId: string) => {
            history.push(`/fleet/machine/v2/${machineId}/machine-health/overview`);
          };
          const itemId = row.original?.id as string;
          const currentAlarms = row.original?.currentAlarms;
          return (
            <button onClick={() => openIssuesOnclick(itemId)}>
              {getValue()}
              {currentAlarms > 0 && (
                <IconContainer>
                  <TriangleWarning />
                  <CounterItem>{currentAlarms}</CounterItem>
                </IconContainer>
              )}
            </button>
          );
        }
      },
      {
        header: capitalizeFirst(t('Status')),
        id: 'status',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => {
          const value = getValue();
          if (!value) {
            return <p>-</p>;
          }
          const Icon = (Icons && Icons[value]) || <></>;
          const color = (colors && colors[value]) || '';
          return (
            <>
              <LineMachineStateIconContainer className="machine-state-icon" {...{ color }}>
                {Icon} <span className="label">{capitalizeFirst(value)}</span>
              </LineMachineStateIconContainer>
            </>
          );
        }
      },
      {
        header: capitalizeFirst(t('Alarms (Past 24h)')),
        id: 'past24hAlarms',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => <p>{getValue() ?? '-'}</p>
      },
      {
        header: capitalizeFirst(t('Configuration type')),
        id: 'configType',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => <p>{getValue() || '-'}</p>
      },
      {
        header: capitalizeFirst(t('Line')),
        id: 'line',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => <p>{getValue() || '-'}</p>
      },
      {
        header: capitalizeFirst(t('Production (Past 24h)')),
        id: 'production24h',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => {
          const value = getValue();
          return value ? <p>{formatMillisecondsToHMS(value)}</p> : '-';
        }
      },
      {
        header: capitalizeFirst(t('Utilization (Past 24h)')),
        id: 'utilization24h',
        isEnableSorting: true,
        isSelected: true,
        renderer: ({ getValue }) => <p>{getValue() || '-'}</p>
      }
    ];
  };

  const tableHeaders = useMemo(() => generateColumnsTable(), [t, type]);

  const Main = useMemo(
    () => (
      <SiteTableWidgetContainer className="widget-ui__main">
        <NewBaseTable
          newTableData={transformedData}
          columnConfigs={tableHeaders}
          sortState={{ id: 'name', desc: false }}
          customClassName="site-line-machine-table"
          maxTableHeight={30}
          tdMarginLeft="0.7rem"
          isDataLoading={machinesLoading || linesLoading}
        />
      </SiteTableWidgetContainer>
    ),
    [transformedData, tableHeaders]
  );

  return <WidgetUi className="site-table-widget" title="" Main={Main} />;
};
